import React, { useState, useEffect } from "react";
import TooltipComponent from "@components/TooltipComponent";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Check, X, Pencil, Info } from "lucide-react";

import { ZERO_VALUE } from "@utils/constants";

const EditableInputComponent = ({
  updatedValue,
  onSave,
  hasToolTip,
  hasLabel,
  className,
  toolTipMessage,
  onLabelClick,
  originalAmount,
  labelValue,
  setIsEditingParent,
}) => {
  const { t: translate } = useTranslation();

  const [value, setValue] = useState(updatedValue);
  const [originalValue, setOriginalValue] = useState(updatedValue);
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
    setIsEditingParent(true);
  };

  const handleSaveClick = () => {
    setIsEditing(false);
    setIsEditingParent(false);
    setValues(value);
  };

  const setValues = (newValue, withSet = false) => {
    if (withSet) {
      setValue(newValue);
    }
    setOriginalValue(newValue);
    onSave(newValue);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setIsEditingParent(false);
    setValue(originalValue);
  };

  return (
    <div>
      {hasLabel && (
        <label className="font-semibold dark:text-white flex items-center gap-2 text-sm text-gray-500 rtl:text-right w-full">
          {labelValue}
          <Info size={18} data-tooltip-id="tooltip-info-destination" />
        </label>
      )}
      {hasToolTip && (
        <TooltipComponent
          anchorElement="tooltip-info-destination"
          toolTipMessage={toolTipMessage}
          position="top"
          className="max-w-96"
        />
      )}
      <div className={clsx("flex gap-2 items-center mt-2")}>
        <input
          data-testid={`editable-input`}
          className="border-2 dark:bg-gray-700 dark:border-gray-600 border-gray-300 rounded-md p-2 w-full"
          type="number"
          value={value}
          readOnly={!isEditing}
          onChange={(e) => {
            const value = e.target.value;
            if (
              value === "" ||
              (Number(value) >= ZERO_VALUE && !value.includes("-"))
            ) {
              setValue(value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "-") {
              e.preventDefault();
            }
          }}
          min="0"
        />
        <div className="min-w-[56px] flex justify-center">
          {isEditing ? (
            <div className="flex gap-2">
              <button data-testid="save-button" onClick={handleSaveClick}>
                <Check stroke="green" />
              </button>
              <button data-testid="cancel-button" onClick={handleCancelClick}>
                <X stroke="red" />
              </button>
            </div>
          ) : (
            <button data-testid="edit-button" onClick={handleEditClick}>
              <Pencil size={20} />
            </button>
          )}
        </div>
      </div>
      <span
        onClick={() => {
          setIsEditing(false);
          setIsEditingParent(false);
          setValues(originalAmount || "", true);
        }}
        className={clsx(
          "text-xs cursor-pointer text-primary-600",
          originalAmount === value && "opacity-0"
        )}
      >
        {translate("confirmation-popup.originalAmount")}
        {originalAmount || ZERO_VALUE}
      </span>
    </div>
  );
};

export default EditableInputComponent;
